import React, { useState } from 'react';
import { IconType } from '@routific/hive';
import { ScheduleIcon } from '@routific/hive/v5';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react';
import { useStore } from '../../../../store/mst';
import ActionButton, { CommonActionTypes } from '../../../shared/ActionButton';
import { ActionButtonProps } from '../types';
import ScheduleWarningModal from '../../../scheduling/ScheduleWarningModal';
import getScheduleButtonTooltip from './getScheduleButtonTooltip';
import isScheduleButtonDisabled from './isScheduleButtonDisabled';
import { isUnscheduledView, View } from '../../../scheduling/ViewMenu';
import { PanelMode } from '../../../shared/Panel';

const ScheduleButton: React.FC<ActionButtonProps> = ({
  type,
  orders,
  routes,
  buttonProps,
  hasPermission = false,
  v5,
  v5Hierarchy,
}) => {
  const store = useStore();
  const { t } = useTranslation('components');

  const [showScheduleWarningModal, setScheduleWarningModal] = useState<boolean>(false);

  const ordersProps = store.model.getOrdersProps(orders);

  const {
    disabled, loading, isLocked, hasScheduled, hasUnscheduled, hasCompleted, isScheduling, isUnscheduling, hasArrivedStop, uuids, isGeocoding,
  } = ordersProps;

  // Calculate actual counts of invalid and questionable orders
  const invalidStopsCount = orders.filter(order => order.isInvalid).length;
  const questionableStopsCount = orders.filter(order => order.isQuestionable).length;
  const hasAddressIssues = invalidStopsCount > 0 || questionableStopsCount > 0;

  const allScheduled = hasScheduled && !hasUnscheduled;

  if (!routes) {
    return null;
  }

  const allOrdersAssignedToRoute = routes.length === 1 && !hasUnscheduled;
  const orderAssignedToOnlyRoute = orders.length === 1 && routes.length === 1 && orders[0].route?.id === routes[0].id;

  const tooltipText = getScheduleButtonTooltip({
    hasPermission,
    hasCompleted,
    hasArrivedStop,
    isGeocoding,
    isLocked,
    isScheduling,
    isUnscheduling,
    orderAssignedToOnlyRoute,
    allOrdersAssignedToRoute,
    allScheduled,
    ordersLength: orders.length,
  });

  const buttonDisabled = isScheduleButtonDisabled({
    disabled,
    routesLength: routes.length,
    orderAssignedToOnlyRoute,
    allOrdersAssignedToRoute,
    hasPermission,
    hasCompleted,
    hasArrivedStop,
  });

  const showSchedulingPreview = () => {
    if (uuids && uuids.length > 0) {
      store.schedulingWorkflow.openSchedulingPreview({ uuids });
    }
  };

  const handleConfirmWarning = async () => {
    setScheduleWarningModal(false);
    showSchedulingPreview();
  };

  const handleToggleFilter = () => {
    setScheduleWarningModal(false);
    const needsViewSwitch = !isUnscheduledView(store.ui.currentView as View);
    if (needsViewSwitch) {
      store.ui.setCurrentView(View.UNSCHEDULED_LIST);
    }
    store.ui.setUnscheduledVisitsPanel(PanelMode.OPEN);
    setTimeout(() => {
      store.ui.setShowQuestionableandInvalidOrders(true);
    }, needsViewSwitch ? 100 : 0);
  };

  const showModal = () => {
    if (!store.ui.disableConfirmScheduleWarningModal && hasAddressIssues) {
      setScheduleWarningModal(true);
    } else {
      showSchedulingPreview();
    }
  };

  return (
    <>
      <ScheduleWarningModal
        visible={showScheduleWarningModal}
        onOk={handleConfirmWarning}
        onCancel={() => setScheduleWarningModal(false)}
        onToggle={handleToggleFilter}
        invalidStopsCount={invalidStopsCount}
        questionableStopsCount={questionableStopsCount}
      />
      <ActionButton
        name={CommonActionTypes.SCHEDULE}
        v5={v5}
        v5Hierarchy={v5Hierarchy}
        v5Icon={<ScheduleIcon size="md" />}
        tooltipProps={{
          title: tooltipText,
        }}
        type={type}
        loading={loading}
        disabled={buttonDisabled}
        menuItemText={allScheduled ? t('scheduleButton.reschedule') : t('scheduleButton.schedule')}
        buttonProps={{
          icon: IconType.Schedule,
          onClick: showModal,
          loading,
          ...buttonProps,
        }}
        textButtonProps={{
          title: allScheduled ? t('scheduleButton.reschedule') : t('scheduleButton.schedule'),
          onClick: showModal,
        }}
      />
    </>
  );
};

export default observer(ScheduleButton);
